/**
 * Thin HTTP client for the M7 agents-runtime REST surface.
 *
 * Authentication: reads the session JWT from {@link tokenStore} (i.e.,
 * `localStorage`) and sends it as `Authorization: Bearer <jwt>` on every
 * request. Same transport as `packages/auth/src/client/auth-client.ts`.
 * Cookies were removed in the auth-redesign — no CSRF token, no
 * `credentials: 'include'`.
 *
 * NestJS error envelopes follow the auth package's shape
 * (`{ error, message, request_id, details? }`); on non-2xx we surface
 * an `AgentsApiError` so React Query hooks can branch on `code` for
 * publish-validation flows (`MODEL_DOES_NOT_SUPPORT_REASONING`, etc.)
 * per `aidlc-docs/construction/agents-runtime/functional-design/agents-rest-api.md`.
 *
 * On 401: clear the stored token (it's invalid) and let the error
 * propagate. The dashboard's route guards detect the missing token on
 * the next render and redirect to `/login`.
 */

import { clearToken, getToken } from "@cometchat-ai/auth/client";

export interface AgentsApiErrorDetails {
  readonly code?: string;
  readonly field?: string;
  readonly [key: string]: unknown;
}

export class AgentsApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string;
  readonly details?: AgentsApiErrorDetails;

  constructor(args: {
    status: number;
    code: string;
    message: string;
    requestId: string;
    details?: AgentsApiErrorDetails;
  }) {
    super(args.message);
    this.name = "AgentsApiError";
    this.status = args.status;
    this.code = args.code;
    this.requestId = args.requestId;
    this.details = args.details;
  }
}

export function readBaseUrl(): string {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  return (env?.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
}

interface ErrorEnvelope {
  readonly error?: string;
  readonly message?: string;
  readonly request_id?: string;
  readonly details?: AgentsApiErrorDetails;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/**
 * Execute a JSON request and throw `AgentsApiError` on non-2xx so the
 * mutation onError handlers and inline form-validation paths can branch
 * on the structured `code` field.
 */
export async function apiRequest<T>(
  method: string,
  path: string,
  body?: unknown,
  search?: Record<string, string | number | undefined>,
  init?: { signal?: AbortSignal },
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let url = `${readBaseUrl()}${path}`;
  if (search) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(search)) {
      if (v !== undefined && v !== "") params.set(k, String(v));
    }
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }

  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal: init?.signal,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const parsed = text.length > 0 ? (safeParse(text) as unknown) : undefined;

  if (!res.ok) {
    if (res.status === 401) clearToken();
    const env = (parsed ?? {}) as ErrorEnvelope;
    throw new AgentsApiError({
      status: res.status,
      code: env.error ?? `HTTP_${res.status}`,
      message: env.message ?? res.statusText ?? "Request failed",
      requestId: env.request_id ?? "",
      details: env.details,
    });
  }

  return parsed as T;
}
